'use client';

import React from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
	CheckCircle,
	Calendar,
	Users,
	TrendingUp,
	Clock,
	MessageSquare,
	PhoneCall,
	BarChart,
	Star,
} from 'lucide-react';
import { ContactForm } from '@/components/ContactForm';
import { Card } from '@/components/ui/card';
import { CardContent } from '@/components/ui/card';
import brand from '@/config/brand'

// Helper function to get icon component from icon name
const getIconComponent = (iconName: string) => {
	switch (iconName) {
		case 'PhoneCall':
			return PhoneCall;
		case 'Clock':
			return Clock;
		case 'Users':
			return Users;
		default:
			return MessageSquare; // fallback icon
	}
};

// Default content for backward compatibility
const defaultHomepageContent = {
	hero: {
		badge: 'Solução Completa',
		title: `Simplifique seus agendamentos com ${brand.appName}`,
		description: `Aumente a produtividade e ofereça a melhor experiência aos seus clientes com o ${brand.appName}.`,
		ctaPrimary: 'Começar Gratuitamente',
		ctaSecondary: 'Agendar Demonstração',
		features: ['Sem cartão de crédito', 'Configuração em 5 minutos'],
	},
	statistics: [
		{ value: '500+', label: 'Negócios' },
		{ value: '50k+', label: 'Agendamentos' },
		{ value: '98%', label: 'Satisfação' },
	],
	painPoints: [
		{
			title: 'Marcações por Telefone',
			description: 'Interromper atendimentos para atender chamadas resulta em clientes insatisfeitos e perda de concentração.',
			icon: 'PhoneCall',
			quote: {
				text: 'Perdemos tempo atendendo chamadas para agendamentos',
				author: 'Profissional',
				business: 'Negócio Local',
			},
		},
		{
			title: 'Tempo Perdido',
			description: 'Gerir horários manualmente consome tempo que poderia usar para atender mais clientes.',
			icon: 'Clock',
			stats: [
				{ label: 'Tempo perdido por semana:', value: '5 horas' },
				{ label: 'Clientes perdidos por mês:', value: '20+' },
			],
		},
		{
			title: 'Clientes Ausentes',
			description: 'Faltas e cancelamentos de última hora causam prejuízos e horários vazios que poderiam ser preenchidos.',
			icon: 'Users',
			quote: {
				text: 'Sistema de agendamento online reduziu significativamente as faltas',
				author: 'Profissional',
				business: 'Negócio Moderno',
			},
		},
	],
	solution: {
		badge: 'A Solução',
		title: `${brand.appName}: O seu assistente digital`,
		description: 'Simplificamos a gestão do seu negócio para que possa focar-se no que realmente importa.',
		features: [
			{
				title: 'Agendamento Online 24/7',
				description: 'Os seus clientes podem marcar horários a qualquer momento, sem precisar telefonar.',
			},
			{
				title: 'Gestão de Clientes',
				description: 'Aceda ao histórico de agendamentos e preferências de cada cliente.',
			},
			{
				title: 'Lembretes Automáticos',
				description: 'Reduza as faltas com lembretes por email ou SMS.',
			},
		],
	},
	testimonials: {
		title: 'O que os nossos clientes dizem',
		description: `Profissionais que confiam no ${brand.appName} para crescer.`,
	},
	finalCta: {
		title: 'Pronto para simplificar seus agendamentos?',
		description: `Junte-se a profissionais que já estão a crescer com o ${brand.appName}.`,
		ctaPrimary: 'Começar Gratuitamente',
		ctaSecondary: 'Falar com Consultor',
	},
	contact: {
		title: 'Fale Conosco',
	},
};

// Get homepage content from brand config or use defaults
const homepageContent = brand.homepage || defaultHomepageContent;

export default function PageClient({
	children,
}: {
	children: React.ReactNode;
}) {
	return (
		<>
			<main className="flex-1">
				{/* Hero Section */}
				<section className="bg-gray-50 py-20">
					<div className="container px-4 md:px-6">
						<div className="grid gap-6 lg:grid-cols-[1fr_400px] lg:gap-12 xl:grid-cols-[1fr_600px]">
							<div className="flex flex-col justify-center space-y-4">
								<div className="space-y-2">
									<Badge className="bg-black text-white hover:bg-gray-800 inline-block">
										{homepageContent.hero.badge}
									</Badge>
									<h1 className="text-3xl font-bold tracking-tighter sm:text-5xl xl:text-6xl/none">
										{homepageContent.hero.title}
									</h1>
									<p className="max-w-[600px] text-gray-500 md:text-xl">
										{homepageContent.hero.description}
									</p>
								</div>
								<div className="flex flex-col gap-2 min-[400px]:flex-row">
									<Link href="/auth/register">
										<Button
											size="lg"
											className="bg-black text-white hover:bg-gray-800 text-lg px-8"
										>
											{homepageContent.hero.ctaPrimary}
										</Button>
									</Link>
									<Button
										asChild
										variant="outline"
										size="lg"
										className="text-lg px-8"
									>
										<a href="#contact">{homepageContent.hero.ctaSecondary}</a>
									</Button>
								</div>
								<div className="flex items-center gap-4 text-sm">
									{homepageContent.hero.features.map((feature, index) => (
										<div key={index} className="flex items-center gap-1">
											<CheckCircle className="h-3 w-3 text-green-500" />
											<span>{feature}</span>
										</div>
									))}
								</div>
								<div className="mt-8 flex items-center gap-8">
									{homepageContent.statistics.map((stat, index) => (
										<div key={index} className="flex flex-col">
											<span className="text-3xl font-bold">{stat.value}</span>
											<span className="text-sm text-gray-500">{stat.label}</span>
										</div>
									))}
								</div>
							</div>
							<div className="flex items-center justify-center">
								{/* TODO: Substituir por um screenshot real do dashboard da aplicação */}
                                                                <div className="relative h-[350px] w-full lg:h-[400px] xl:h-[500px] overflow-hidden rounded-lg border bg-white shadow-lg">
                                                                        <Image
                                                                               src={brand.heroImage.src}
                                                                               alt={brand.heroImage.alt}
                                                                               fill
                                                                               className="object-cover pointer-events-none"
                                                                        />
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Pain Points Section */}
				<section className="py-20">
					<div className="container px-4 md:px-6">
						<div className="flex flex-col items-center justify-center space-y-4 text-center">
							<div className="space-y-2">
								<h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
									Problemas comuns do seu negócio
								</h2>
								<p className="max-w-[700px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
									Reconhece algum destes desafios no seu dia-a-dia?
								</p>
							</div>
						</div>
						<div className="mx-auto grid max-w-5xl gap-6 py-12 lg:grid-cols-3">
							{homepageContent.painPoints.map((painPoint, index) => {
								const IconComponent = getIconComponent(painPoint.icon);
								return (
									<Card key={index} className="hover:shadow-lg transition-shadow">
										<CardContent className="p-6">
											<div className="mb-4 rounded-full bg-gray-100 p-2 w-10 h-10 flex items-center justify-center">
												<IconComponent className="h-5 w-5" />
											</div>
											<h3 className="text-xl font-bold">{painPoint.title}</h3>
											<p className="text-gray-500 mt-2">
												{painPoint.description}
											</p>
											{painPoint.quote && (
												<div className="mt-4 p-4 bg-gray-50 rounded-lg">
													<p className="text-sm text-gray-600 italic">
														&ldquo;{painPoint.quote.text}&rdquo;
													</p>
													<p className="text-sm font-medium mt-2">
														- {painPoint.quote.author}, {painPoint.quote.business}
													</p>
												</div>
											)}
											{painPoint.stats && (
												<div className="mt-4">
													{painPoint.stats.map((stat, statIndex) => (
														<div key={statIndex} className="flex items-center justify-between text-sm mt-2">
															<span className="text-gray-600">
																{stat.label}
															</span>
															<span className="font-bold">{stat.value}</span>
														</div>
													))}
												</div>
											)}
										</CardContent>
									</Card>
								);
							})}
						</div>
					</div>
				</section>

				{/* Solution Section */}
				<section className="bg-gray-50 py-20" id="features">
					<div className="container px-4 md:px-6">
						<div className="flex flex-col items-center justify-center space-y-4 text-center">
							<div className="space-y-2">
								<Badge className="bg-black text-white hover:bg-gray-800 inline-block">
									{homepageContent.solution.badge}
								</Badge>
								<h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
									{homepageContent.solution.title}
								</h2>
								<p className="max-w-[700px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
									{homepageContent.solution.description}
								</p>
							</div>
						</div>
						<div className="grid gap-6 pt-12 lg:grid-cols-3 lg:gap-12">
							<div className="flex flex-col justify-center space-y-4">
								<ul className="grid gap-6">
									{homepageContent.solution.features.map((feature, index) => (
										<li key={index}>
											<div className="grid gap-1">
												<div className="flex items-center gap-2">
													<CheckCircle className="h-5 w-5 text-green-500" />
													<h3 className="text-xl font-bold">
														{feature.title}
													</h3>
												</div>
												<p className="text-gray-500">
													{feature.description}
												</p>
											</div>
										</li>
									))}
								</ul>
							</div>
							<div className="lg:col-span-2">
								{/* TODO: Substituir por um screenshot real do calendário de agendamentos */}
                                                                <div className="relative h-[300px] w-full lg:h-[400px] overflow-hidden rounded-lg border bg-white shadow-lg">
                                                                        <Image
                                                                               src={brand.serviceImage.src}
                                                                               alt={brand.serviceImage.alt}
                                                                               fill
                                                                               className="object-cover pointer-events-none"
                                                                        />
								</div>
							</div>
						</div>
					</div>
				</section>

				{/* Testimonials */}
				<section className="py-20">
					<div className="container px-4 md:px-6">
						<div className="flex flex-col items-center justify-center space-y-4 text-center">
							<div className="space-y-2">
								<h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
									{homepageContent.testimonials.title}
								</h2>
								<p className="max-w-[700px] text-gray-500 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
									{homepageContent.testimonials.description}
								</p>
							</div>
						</div>
						<div className="mx-auto grid max-w-5xl gap-6 py-12 lg:grid-cols-3">
							{brand.testimonials.map((testimonial, index) => (
								<Card key={index}>
									<CardContent className="p-6">
										<div className="flex items-center gap-4">
											<div className="h-12 w-12 rounded-full bg-gray-200 flex items-center justify-center">
												<Image
													src={testimonial.image}
													alt={`Avatar ${testimonial.name}`}
													width={48}
													height={48}
													className="rounded-full"
												/>
											</div>
											<div>
												<h4 className="font-bold">{testimonial.name}</h4>
												<p className="text-sm text-gray-500">
													{testimonial.role}
												</p>
											</div>
										</div>
										<blockquote className="mt-4 italic text-gray-700">
											&quot;{testimonial.text}&quot;
										</blockquote>
									</CardContent>
								</Card>
							))}
						</div>
					</div>
				</section>

				{children}

				{/* Final CTA */}
				<section className="bg-black py-20 text-white">
					<div className="container px-4 md:px-6">
						<div className="flex flex-col items-center justify-center space-y-4 text-center">
							<div className="space-y-2">
								<h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl">
									{homepageContent.finalCta.title}
								</h2>
								<p className="mx-auto max-w-[700px] text-gray-300 md:text-xl/relaxed lg:text-base/relaxed xl:text-xl/relaxed">
									{homepageContent.finalCta.description}
								</p>
							</div>
							<div className="flex flex-col gap-2 min-[400px]:flex-row">
								<Link href="/auth/register">
									<Button className="bg-white text-black hover:bg-gray-100">
										{homepageContent.finalCta.ctaPrimary}
									</Button>
								</Link>
								<Button
									asChild
									className="bg-gray-700 text-white hover:bg-gray-600 border-0"
								>
									<a href="#contact">{homepageContent.finalCta.ctaSecondary}</a>
								</Button>
							</div>
						</div>
					</div>
				</section>
			</main>

			{/* Contact Section */}
			<section id="contact" className="py-20 bg-gray-50">
				<div className="container px-4 md:px-6">
					<h2 className="text-3xl font-bold tracking-tighter sm:text-4xl md:text-5xl text-center mb-8">
						{homepageContent.contact.title}
					</h2>
					<div className="max-w-lg mx-auto">
						<ContactForm />
					</div>
				</div>
			</section>
		</>
	);
}
